import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL DESIGN SYSTEM — THEME TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  card: "#FFFFFF",
  cardBorder: "#E2E5EC",
  hover: "#F0F1F5",
  accent: "#D23A3B",
  accentSoft: "rgba(210,58,59,0.08)",
  accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A",
  greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626",
  redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706",
  orangeSoft: "rgba(217,119,6,0.08)",
  t1: "#1A1D2E",
  t2: "#5A6178",
  t3: "#9CA3B8",
  divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
  cardShadow: "0 1px 4px rgba(0,0,0,0.05)",
  sidebarShadow: "2px 0 8px rgba(0,0,0,0.03)",
  topbarShadow: "0 1px 3px rgba(0,0,0,0.04)",
  font: "'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  mono: "'DM Mono','SF Mono','Consolas',monospace",
};

/* ═══════════════════════════════════════════════════════════════════════════
   MOCK DATA
   ═══════════════════════════════════════════════════════════════════════════ */
const MOCK = {
  kpis: [
    { key: "totalRes",   label: "TOTAL RESERVATIONS", value: "1,284",  delta: 8.3,  emoji: "📋" },
    { key: "noShow",     label: "NO-SHOW RATE",       value: "6.2%",   delta: -1.4, emoji: "👻" },
    { key: "avgParty",   label: "AVG PARTY SIZE",     value: "3.8",    delta: 0.3,  emoji: "👥" },
    { key: "covers",     label: "UPCOMING COVERS",    value: "86",     delta: null,  emoji: "🪑" },
    { key: "waitTime",   label: "AVG WAIT TIME",      value: "14 min", delta: -2.1, emoji: "⏱" },
    { key: "seatEff",    label: "SEATING EFFICIENCY",  value: "88%",    delta: 3.5,  emoji: "📐" },
  ],
  weeklyTrend: [
    { day: "Mon", reservations: 162, noShows: 12 },
    { day: "Tue", reservations: 178, noShows: 9 },
    { day: "Wed", reservations: 195, noShows: 14 },
    { day: "Thu", reservations: 210, noShows: 11 },
    { day: "Fri", reservations: 248, noShows: 18 },
    { day: "Sat", reservations: 272, noShows: 16 },
    { day: "Sun", reservations: 198, noShows: 10 },
  ],
  turnTimes: [
    { hour: "11a", actual: 52, estimated: 48 },
    { hour: "12p", actual: 68, estimated: 55 },
    { hour: "1p",  actual: 62, estimated: 55 },
    { hour: "2p",  actual: 45, estimated: 48 },
    { hour: "5p",  actual: 58, estimated: 52 },
    { hour: "6p",  actual: 72, estimated: 55 },
    { hour: "7p",  actual: 78, estimated: 55 },
    { hour: "8p",  actual: 65, estimated: 55 },
    { hour: "9p",  actual: 50, estimated: 48 },
  ],
  channelMix: [
    { name: "Website Widget", value: 42, color: T.accent },
    { name: "Maghil App",     value: 28, color: "#E86B6B" },
    { name: "Google Reserve",  value: 18, color: T.orange },
    { name: "Phone / Walk-in", value: 12, color: T.t3 },
  ],
  feedback: { avgRating: 4.2, nps: 62, responses: 847, alerts: 14 },
  floorStatus: { total: 42, occupied: 31, available: 6, bussing: 3, blocked: 2 },
  hourlyCovers: [
    { hour: "11a", covers: 12 }, { hour: "12p", covers: 28 },
    { hour: "1p", covers: 22 },  { hour: "2p", covers: 14 },
    { hour: "5p", covers: 18 },  { hour: "6p", covers: 42 },
    { hour: "7p", covers: 56 },  { hour: "8p", covers: 48 },
    { hour: "9p", covers: 28 },
  ],
  heatmap: {
    days: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"],
    hours: ["11a","12p","1p","2p","5p","6p","7p","8p","9p"],
    data: [
      [0.12,0.35,0.28,0.10,0.22,0.52,0.48,0.38,0.18],
      [0.15,0.38,0.30,0.12,0.25,0.55,0.50,0.40,0.20],
      [0.18,0.42,0.35,0.15,0.30,0.58,0.55,0.45,0.22],
      [0.20,0.45,0.38,0.18,0.35,0.62,0.60,0.50,0.28],
      [0.28,0.52,0.45,0.22,0.42,0.75,0.85,0.70,0.38],
      [0.35,0.58,0.50,0.28,0.48,0.82,0.95,0.78,0.42],
      [0.22,0.40,0.35,0.18,0.30,0.55,0.50,0.42,0.25],
    ],
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

function Card({ children, style, drillable, mobile }: any) {
  return (
    <div style={{
      background: T.card,
      border: `1px solid ${drillable ? "rgba(210,58,59,0.2)" : T.cardBorder}`,
      borderRadius: 14,
      padding: mobile ? 14 : 22,
      boxShadow: mobile ? T.shadow : T.cardShadow,
      position: "relative",
      ...style,
    }}>
      {drillable && (
        <svg width="10" height="10" viewBox="0 0 10 10" style={{ position:"absolute", top:8, right:8, opacity:0.45 }}>
          <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
      {children}
    </div>
  );
}

function KPIMobile({ label, value, delta }: any) {
  const hasDelta = delta !== null && delta !== undefined;
  const pos = hasDelta && delta >= 0;
  const dColor = hasDelta ? (pos ? T.green : T.red) : null;
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.cardBorder}`,
      borderRadius: hasDelta ? 12 : 10,
      padding: hasDelta ? "12px 10px" : 10,
      boxShadow: T.shadow, textAlign: "center",
    }}>
      <div style={{ fontSize:8, fontWeight:400, color:T.t3, textTransform:"uppercase", letterSpacing:0.3 }}>{label}</div>
      <div style={{ fontSize:18, fontWeight:700, color:T.t1, marginTop: hasDelta ? 4 : 3 }}>{value}</div>
      {hasDelta && (
        <div style={{ fontSize:9, fontWeight:600, color:dColor, marginTop:3 }}>
          {pos ? "↑" : "↓"} {pos ? "+" : ""}{delta}%
        </div>
      )}
    </div>
  );
}

function KPIDesktop({ label, value, delta, emoji }: any) {
  const hasDelta = delta !== null && delta !== undefined;
  const pos = hasDelta && delta >= 0;
  const dColor = hasDelta ? (pos ? T.green : T.red) : null;
  const blurColor = hasDelta ? (pos ? T.greenSoft : T.redSoft) : T.accentSoft;
  return (
    <div style={{
      flex: 1, minWidth: 155, background: T.card,
      border: `1px solid ${T.cardBorder}`, borderRadius: 14,
      padding: "20px 22px", boxShadow: T.cardShadow,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position:"absolute", top:-20, right:-20,
        width:80, height:80, borderRadius:"50%",
        background: blurColor, filter:"blur(20px)",
      }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", position:"relative" }}>
        <div style={{ fontSize:12, fontWeight:500, color:T.t2, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
        {emoji && <span style={{ fontSize:18 }}>{emoji}</span>}
      </div>
      <div style={{ fontSize:28, fontWeight:700, color:T.t1, letterSpacing:-0.5, marginTop:6, position:"relative" }}>{value}</div>
      {hasDelta && (
        <div style={{ fontSize:12, fontWeight:600, color:dColor, marginTop:4, position:"relative" }}>
          {pos ? "↑" : "↓"} {pos ? "+" : ""}{delta}% vs prior period
        </div>
      )}
    </div>
  );
}

function SecTitle({ children, sub, mobile }: any) {
  return (
    <div style={{ marginBottom: mobile ? 8 : 12 }}>
      <div style={{ fontSize: mobile ? 12 : 14, fontWeight:600, color:T.t1 }}>{children}</div>
      {sub && <div style={{ fontSize: mobile ? 9 : 11, fontWeight:400, color:T.t3, marginTop:2 }}>{sub}</div>}
    </div>
  );
}

function MTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background:T.surface, border:`1px solid ${T.cardBorder}`,
      borderRadius:8, padding:"8px 12px", boxShadow:T.cardShadow, fontFamily:T.font,
    }}>
      <div style={{ fontSize:11, fontWeight:600, color:T.t1, marginBottom:4 }}>{label}</div>
      {payload.map((p,i)=>(
        <div key={i} style={{ fontSize:10, color: p.color || T.t2, marginTop:2 }}>
          {p.name}: <span style={{ fontWeight:600, color:T.t1 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CHART & WIDGET COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

function WeeklyTrend({ mobile }: any) {
  const d = MOCK.weeklyTrend;
  const peakI = d.reduce((m,x,i,a)=> x.reservations > a[m].reservations ? i : m, 0);
  return (
    <Card mobile={mobile} style={{ flex: mobile ? "1 1 auto" : 2 }}>
      <SecTitle sub="7-day reservation volume & no-shows" mobile={mobile}>Weekly Trend</SecTitle>
      <div style={{ height: mobile ? 100 : 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d} barGap={mobile ? 1 : 2}>
            <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5}/>
            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: mobile ? 7 : 9, fill:T.t3, fontFamily:T.font }}/>
            <YAxis hide/>
            <Tooltip content={<MTooltip/>}/>
            <Bar dataKey="reservations" name="Reservations" radius={[3,3,0,0]} maxBarSize={mobile ? 18 : 24}>
              {d.map((_,i)=><Cell key={i} fill={i===peakI ? T.accent : "rgba(210,58,59,0.4)"}/>)}
            </Bar>
            <Bar dataKey="noShows" name="No-Shows" radius={[3,3,0,0]} maxBarSize={mobile ? 12 : 16} fill="rgba(220,38,38,0.5)"/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function TurnTime({ mobile }: any) {
  return (
    <Card mobile={mobile} style={{ flex: mobile ? "1 1 auto" : 1 }}>
      <SecTitle sub="Actual vs estimated (min)" mobile={mobile}>Turn Time Accuracy</SecTitle>
      <div style={{ height: mobile ? 100 : 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={MOCK.turnTimes}>
            <defs>
              <linearGradient id="ttG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={T.accent} stopOpacity={0.15}/>
                <stop offset="95%" stopColor={T.accent} stopOpacity={0.01}/>
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5}/>
            <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: mobile ? 7 : 9, fill:T.t3, fontFamily:T.font }}/>
            <YAxis hide/>
            <Tooltip content={<MTooltip/>}/>
            <Area type="monotone" dataKey="actual" name="Actual" stroke={T.accent} strokeWidth={2.5} fill="url(#ttG)" dot={false}/>
            <Area type="monotone" dataKey="estimated" name="Estimated" stroke={T.t3} strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={false}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function ChannelDonut({ mobile }: any) {
  const d = MOCK.channelMix;
  return (
    <Card mobile={mobile} style={{ flex:1 }}>
      <SecTitle sub="Booking source distribution" mobile={mobile}>Reservation Channels</SecTitle>
      <div style={{ display:"flex", flexDirection: mobile ? "column" : "row", alignItems:"center", gap: mobile ? 12 : 24 }}>
        <div style={{ width: mobile ? 110 : 140, height: mobile ? 110 : 140, flexShrink:0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={d} cx="50%" cy="50%" innerRadius={mobile ? 34 : 42} outerRadius={mobile ? 50 : 62} dataKey="value" strokeWidth={0}>
                {d.map((e,i)=><Cell key={i} fill={e.color}/>)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {d.map((e,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ width:8, height:8, borderRadius:2, background:e.color, flexShrink:0 }}/>
              <span style={{ fontSize:11, color:T.t2, width: mobile ? 95 : 110 }}>{e.name}</span>
              <span style={{ fontSize:11, fontWeight:600, color:T.t1 }}>{e.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function Feedback({ mobile }: any) {
  const f = MOCK.feedback;
  const stars = Array.from({length:5},(_,i)=> i < Math.round(f.avgRating));
  const items = [
    { v: f.nps, l: "NPS SCORE", c: T.green },
    { v: f.responses, l: "RESPONSES", c: T.t1 },
    { v: f.alerts, l: "ALERTS SENT", c: T.red },
  ];
  return (
    <Card mobile={mobile} style={{ flex:1 }}>
      <SecTitle sub="Post-checkout survey results" mobile={mobile}>Guest Feedback</SecTitle>
      <div style={{ display:"flex", flexWrap:"wrap", gap: mobile ? 10 : 20, alignItems:"flex-start" }}>
        <div style={{ textAlign:"center", minWidth:70 }}>
          <div style={{ fontSize: mobile ? 28 : 34, fontWeight:700, color:T.t1, letterSpacing:-1 }}>{f.avgRating}</div>
          <div style={{ marginTop:2, display:"flex", justifyContent:"center", gap:2 }}>
            {stars.map((filled,i)=><span key={i} style={{ color: filled ? T.accent : T.hover, fontSize: mobile ? 12 : 14 }}>★</span>)}
          </div>
          <div style={{ fontSize:8, fontWeight:400, color:T.t3, textTransform:"uppercase", marginTop:4, letterSpacing:0.3 }}>AVG RATING</div>
        </div>
        <div style={{ display:"flex", gap: mobile ? 12 : 24, flexWrap:"wrap" }}>
          {items.map((it,i)=>(
            <div key={i} style={{ textAlign:"center" }}>
              <div style={{ fontSize: mobile ? 18 : 22, fontWeight:700, color:it.c }}>{it.v}</div>
              <div style={{ fontSize:8, color:T.t3, textTransform:"uppercase", letterSpacing:0.3 }}>{it.l}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function FloorStatus({ mobile }: any) {
  const f = MOCK.floorStatus;
  const pct = Math.round((f.occupied / f.total) * 100);
  const segs = [
    { label:"Occupied", count:f.occupied, color:"#4A7FE5" },
    { label:"Available", count:f.available, color:T.green },
    { label:"Bussing", count:f.bussing, color:T.orange },
    { label:"Blocked", count:f.blocked, color:T.t3 },
  ];
  return (
    <Card mobile={mobile} style={{ flex:1 }}>
      <SecTitle sub={`${f.total} total tables`} mobile={mobile}>Floor Status</SecTitle>
      <div style={{ display:"flex", height: mobile ? 10 : 12, borderRadius:6, overflow:"hidden", marginBottom: mobile ? 10 : 14 }}>
        {segs.map((s,i)=><div key={i} style={{ flex:s.count, background:s.color, opacity:0.7 }}/>)}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: mobile ? 8 : 12 }}>
        {segs.map((s,i)=>(
          <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", background:s.color, flexShrink:0 }}/>
            <div>
              <div style={{ fontSize: mobile ? 14 : 16, fontWeight:700, color:T.t1 }}>{s.count}</div>
              <div style={{ fontSize:8, color:T.t3, textTransform:"uppercase", letterSpacing:0.3 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: mobile ? 10 : 14, padding:"8px 12px", background:T.accentSoft, borderRadius:8, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize: mobile ? 20 : 24, fontWeight:700, color:T.accent }}>{pct}%</span>
        <span style={{ fontSize: mobile ? 10 : 11, color:T.t2 }}>current occupancy</span>
      </div>
    </Card>
  );
}

function HourlyCovers({ mobile }: any) {
  const d = MOCK.hourlyCovers;
  const peakI = d.reduce((m,x,i,a)=> x.covers > a[m].covers ? i : m, 0);
  return (
    <Card mobile={mobile} style={{ flex: mobile ? "1 1 auto" : 1 }}>
      <SecTitle sub="Expected covers by hour" mobile={mobile}>Shift Forecast</SecTitle>
      <div style={{ height: mobile ? 100 : 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d}>
            <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5}/>
            <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: mobile ? 7 : 9, fill:T.t3, fontFamily:T.font }}/>
            <YAxis hide/>
            <Tooltip content={<MTooltip/>}/>
            <Bar dataKey="covers" name="Covers" radius={[3,3,0,0]} maxBarSize={mobile ? 18 : 24}>
              {d.map((_,i)=><Cell key={i} fill={i===peakI ? T.green : "rgba(22,163,74,0.4)"}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function Heatmap() {
  const { days, hours, data } = MOCK.heatmap;
  return (
    <Card mobile={false}>
      <SecTitle sub="Density by day and hour" mobile={false}>Reservation Heatmap</SecTitle>
      <div style={{ display:"flex", gap:3 }}>
        <div style={{ width:28, display:"flex", flexDirection:"column", gap:3, paddingTop:20 }}>
          {days.map(d=><div key={d} style={{ height:18, fontSize:9, color:T.t3, display:"flex", alignItems:"center" }}>{d}</div>)}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", gap:3, marginBottom:3 }}>
            {hours.map(h=><div key={h} style={{ flex:1, fontSize:9, color:T.t3, textAlign:"center" }}>{h}</div>)}
          </div>
          {data.map((row,ri)=>(
            <div key={ri} style={{ display:"flex", gap:3, marginBottom:3 }}>
              {row.map((v,ci)=>(
                <div key={ci} style={{
                  flex:1, height:18, borderRadius:3,
                  background: `rgba(210,58,59,${Math.max(0.06, v * 0.65)})`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:8, fontWeight:600, color: v >= 0.7 ? "#fff" : "transparent",
                }}>{v >= 0.7 ? Math.round(v*100) : ""}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT SHELLS
   ═══════════════════════════════════════════════════════════════════════════ */

function DesktopSidebar() {
  const groups = [
    { emoji:"📊", label:"Sales", items:["Sales Summary","Hourly Sales","Order Channels"] },
    { emoji:"🍽️", label:"Menu", items:["Item Sales","Category Sales"] },
    { emoji:"👥", label:"Team", items:["Staff Performance"] },
    { emoji:"💰", label:"Payments", items:["Tender Types"] },
    { emoji:"🪑", label:"Guests", items:["Check-In Analytics","Reservations"] },
    { emoji:"📍", label:"Locations", items:["Location Benchmark"] },
  ];
  return (
    <div style={{
      width:250, background:T.surface, borderRight:`1px solid ${T.divider}`,
      boxShadow:T.sidebarShadow, display:"flex", flexDirection:"column", flexShrink:0,
    }}>
      <div style={{ padding:20, borderBottom:`1px solid ${T.divider}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:34, height:34, borderRadius:10, background:"linear-gradient(135deg,#D23A3B,#E86B6B)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:16 }}>M</div>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:T.t1 }}>Maghil</div>
            <div style={{ fontSize:10, fontWeight:400, color:T.t3, letterSpacing:0.5 }}>ANALYTICS</div>
          </div>
        </div>
      </div>
      <div style={{ margin:"12px 10px", padding:"5px 10px", background:T.hover, border:`1px solid ${T.cardBorder}`, borderRadius:8, display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
        <span style={{ fontSize:11 }}>📍</span>
        <span style={{ fontSize:11, fontWeight:600, color:T.t1, flex:1 }}>Downtown Bistro</span>
        <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={T.t3} strokeWidth="1.3" strokeLinecap="round"/></svg>
      </div>
      <div style={{ flex:1, padding:"8px 0", overflow:"auto" }}>
        {groups.map(g=>(
          <div key={g.label} style={{ marginBottom:4 }}>
            <div style={{ padding:"6px 16px", fontSize:12.5, fontWeight:600, color:T.t3, display:"flex", alignItems:"center", gap:6 }}><span>{g.emoji}</span>{g.label}</div>
            {g.items.map(item=>{
              const active = item === "Reservations";
              return (
                <div key={item} style={{
                  padding:"8px 10px 8px 22px", fontSize:12,
                  fontWeight: active ? 600 : 400,
                  color: active ? T.accent : T.t2,
                  background: active ? T.accentSoft : "transparent",
                  borderLeft: active ? `2px solid ${T.accent}` : "2px solid transparent",
                  cursor:"pointer",
                }}>{item}</div>
              );
            })}
          </div>
        ))}
      </div>
      <div style={{ padding:"12px 16px", borderTop:`1px solid ${T.divider}`, display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ width:28, height:28, borderRadius:"50%", background:T.accentSoft, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:600, color:T.accent }}>JM</div>
        <div><div style={{ fontSize:12, fontWeight:600, color:T.t1 }}>Jordan M.</div><div style={{ fontSize:10, color:T.t3 }}>Manager</div></div>
      </div>
    </div>
  );
}

function DesktopTopBar({ period, onPeriod }: any) {
  const periods = ["Today","This Week","This Month","Custom"];
  return (
    <div style={{
      height:56, background:T.surface, borderBottom:`1px solid ${T.divider}`,
      boxShadow:T.topbarShadow, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 24px",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
        <span style={{ fontSize:12, color:T.t3 }}>Reports &gt; Guests &gt;</span>
        <span style={{ fontSize:14, fontWeight:700, color:T.t1 }}>Reservations</span>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ display:"flex", gap:4 }}>
          {periods.map(p=>(
            <button key={p} onClick={()=>onPeriod(p)} style={{
              border:"none", borderRadius:6, padding:"4px 12px",
              background: period===p ? T.accentSoft : "transparent",
              color: period===p ? T.accent : T.t2,
              fontSize:11.5, fontWeight: period===p ? 600 : 500,
              cursor:"pointer", fontFamily:T.font,
            }}>{p}</button>
          ))}
        </div>
        <button style={{ border:`1px solid ${T.cardBorder}`, background:T.surface, borderRadius:8, padding:"5px 12px", fontSize:11.5, color:T.t2, cursor:"pointer", fontFamily:T.font }}>Export ↓</button>
      </div>
    </div>
  );
}

function MobHeader() {
  return (
    <div style={{ padding:"8px 16px 0" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:700, color:T.t1 }}>Reservations</div>
          <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:2 }}>
            <span style={{ fontSize:10 }}>📍</span>
            <span style={{ fontSize:11, fontWeight:600, color:T.t1 }}>Downtown Bistro</span>
          </div>
        </div>
        <div style={{ width:30, height:30, borderRadius:8, background:T.accentSoft, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:600, color:T.accent }}>JM</div>
      </div>
      <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:10, marginBottom:6 }}>
        <button style={{ width:26, height:26, borderRadius:7, background:T.hover, border:`1px solid ${T.divider}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:12, color:T.t3, fontFamily:T.font }}>‹</button>
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 12px", borderRadius:8, background:T.surface, border:`1px solid ${T.divider}` }}>
          <svg width="12" height="12" viewBox="0 0 12 12"><rect x="1" y="2" width="10" height="9" rx="1.5" fill="none" stroke={T.t3} strokeWidth="1"/><line x1="1" y1="5" x2="11" y2="5" stroke={T.t3} strokeWidth="0.8"/><line x1="4" y1="1" x2="4" y2="3" stroke={T.t3} strokeWidth="1" strokeLinecap="round"/><line x1="8" y1="1" x2="8" y2="3" stroke={T.t3} strokeWidth="1" strokeLinecap="round"/></svg>
          <span style={{ fontSize:12, fontWeight:600, color:T.t1 }}>Today</span>
          <span style={{ fontSize:11, color:T.t3 }}>Mar 3, 2026</span>
        </div>
        <button style={{ width:26, height:26, borderRadius:7, background:T.hover, border:`1px solid ${T.divider}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:12, color:T.t3, opacity:0.3, fontFamily:T.font }}>›</button>
      </div>
    </div>
  );
}

function BotNav() {
  const tabs = [
    { emoji:"⚡", label:"Snapshot", active:false },
    { emoji:"📊", label:"Reports", active:true },
    { emoji:"✨", label:"Maghil AI", active:false },
    { emoji:"💬", label:"Support", active:false },
  ];
  return (
    <div style={{
      height:72, background:T.surface, borderTop:`1px solid ${T.divider}`,
      display:"flex", alignItems:"center", justifyContent:"space-around", paddingBottom:8,
    }}>
      {tabs.map(t=>(
        <div key={t.label} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, cursor:"pointer", padding:"4px 14px" }}>
          <span style={{ fontSize:18, opacity:t.active?1:0.4, filter:t.active?"none":"grayscale(100%)" }}>{t.emoji}</span>
          <span style={{ fontSize:9.5, fontWeight:t.active?700:400, color:t.active?T.accent:T.t3 }}>{t.label}</span>
          {t.active && <span style={{ width:4, height:4, borderRadius:"50%", background:T.accent, marginTop:1 }}/>}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONTENT — DESKTOP
   ═══════════════════════════════════════════════════════════════════════════ */
function DeskContent() {
  return (
    <div style={{ padding:24, display:"flex", flexDirection:"column", gap:24 }}>
      <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
        {MOCK.kpis.map(k=><KPIDesktop key={k.key} label={k.label} value={k.value} delta={k.delta} emoji={k.emoji}/>)}
      </div>
      <div style={{ display:"flex", gap:16 }}>
        <WeeklyTrend mobile={false}/>
        <TurnTime mobile={false}/>
      </div>
      <div style={{ display:"flex", gap:16 }}>
        <ChannelDonut mobile={false}/>
        <FloorStatus mobile={false}/>
      </div>
      <div style={{ display:"flex", gap:16 }}>
        <HourlyCovers mobile={false}/>
        <Feedback mobile={false}/>
      </div>
      <Heatmap/>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONTENT — MOBILE
   ═══════════════════════════════════════════════════════════════════════════ */
function MobContent() {
  const kpis = MOCK.kpis;
  const rows = [];
  for (let i = 0; i < kpis.length; i += 3) rows.push(kpis.slice(i, i + 3));
  return (
    <div style={{ padding:"8px 16px 16px", display:"flex", flexDirection:"column", gap:8 }}>
      {rows.map((row,ri)=>(
        <div key={ri} style={{ display:"grid", gridTemplateColumns:`repeat(${row.length},1fr)`, gap:8 }}>
          {row.map(k=><KPIMobile key={k.key} label={k.label} value={k.value} delta={k.delta}/>)}
        </div>
      ))}
      <WeeklyTrend mobile/>
      <TurnTime mobile/>
      <ChannelDonut mobile/>
      <FloorStatus mobile/>
      <HourlyCovers mobile/>
      <Feedback mobile/>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ASSEMBLED SCREENS
   ═══════════════════════════════════════════════════════════════════════════ */
function DeskScreen({ period, onPeriod }: any) {
  return (
    <div style={{ display:"flex", height:"100%", minHeight:900 }}>
      <DesktopSidebar/>
      <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column" }}>
        <DesktopTopBar period={period} onPeriod={onPeriod}/>
        <div style={{ flex:1, overflow:"auto" }}><DeskContent/></div>
      </div>
    </div>
  );
}

function MobScreen() {
  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>
      <MobHeader/>
      <div style={{ flex:1, overflow:"auto" }}><MobContent/></div>
      <BotNav/>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN EXPORT — DUAL PREVIEW
   ═══════════════════════════════════════════════════════════════════════════ */
export default function ReservationDashboard() {
  const [period, setPeriod] = useState("Today");
  const [view, setView] = useState("both");

  return (
    <div style={{ fontFamily:T.font, background:"#E8EAF0", minHeight:"100vh", WebkitFontSmoothing:"antialiased" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');*{box-sizing:border-box;margin:0;padding:0;}body{margin:0;}::-webkit-scrollbar{width:5px;height:5px;}::-webkit-scrollbar-track{background:transparent;}::-webkit-scrollbar-thumb{background:${T.cardBorder};border-radius:3px;}::-webkit-scrollbar-thumb:hover{background:${T.t3};}`}</style>

      {/* Preview Toolbar */}
      <div style={{ background:T.surface, borderBottom:`1px solid ${T.divider}`, padding:"10px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:50 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:28, height:28, borderRadius:8, background:"linear-gradient(135deg,#D23A3B,#E86B6B)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:13 }}>M</div>
          <span style={{ fontSize:14, fontWeight:700, color:T.t1 }}>Reservation Analytics</span>
          <span style={{ fontSize:11, color:T.t3, marginLeft:4 }}>— Screen Preview</span>
        </div>
        <div style={{ display:"flex", gap:4 }}>
          {[
            { key:"both",    label:"Both Screens" },
            { key:"desktop", label:"Desktop Only" },
            { key:"mobile",  label:"Mobile Only" },
          ].map(o=>(
            <button key={o.key} onClick={()=>setView(o.key)} style={{
              border:"none", borderRadius:6, padding:"5px 14px",
              background: view===o.key ? T.accentSoft : "transparent",
              color: view===o.key ? T.accent : T.t2,
              fontSize:11.5, fontWeight: view===o.key ? 600 : 400,
              cursor:"pointer", fontFamily:T.font,
            }}>{o.label}</button>
          ))}
        </div>
      </div>

      {/* Preview Canvas */}
      <div style={{ padding:24, display:"flex", gap:24, justifyContent:"center", alignItems:"flex-start", flexWrap:"wrap" }}>

        {/* Desktop Frame */}
        {(view==="both" || view==="desktop") && (
          <div style={{ flexShrink:0 }}>
            <div style={{ fontSize:11, fontWeight:600, color:T.t3, textTransform:"uppercase", letterSpacing:0.5, marginBottom:8, textAlign:"center" }}>Desktop — 1440 × 900</div>
            <div style={{
              width: view==="both" ? 960 : 1180,
              height: view==="both" ? 660 : 800,
              background:T.bg, borderRadius:12, overflow:"hidden",
              boxShadow:"0 4px 24px rgba(0,0,0,0.10)",
              border:`1px solid ${T.cardBorder}`,
            }}>
              <div style={{ width:"100%", height:"100%", overflow:"auto" }}>
                <DeskScreen period={period} onPeriod={setPeriod}/>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Frame */}
        {(view==="both" || view==="mobile") && (
          <div style={{ flexShrink:0 }}>
            <div style={{ fontSize:11, fontWeight:600, color:T.t3, textTransform:"uppercase", letterSpacing:0.5, marginBottom:8, textAlign:"center" }}>Mobile — 375 × 812</div>
            <div style={{
              width: view==="mobile" ? 390 : 375,
              height: view==="mobile" ? 844 : 740,
              background:T.bg,
              borderRadius: view==="mobile" ? 40 : 28,
              overflow:"hidden",
              boxShadow:"0 4px 24px rgba(0,0,0,0.10)",
              border: view==="mobile" ? "8px solid #1A1D2E" : `1px solid ${T.cardBorder}`,
              position:"relative",
            }}>
              {view==="mobile" && (
                <div style={{ position:"absolute", top:8, left:"50%", transform:"translateX(-50%)", width:120, height:28, background:"#1A1D2E", borderRadius:14, zIndex:10 }}/>
              )}
              <div style={{ width:"100%", height:"100%", overflow:"auto", paddingTop: view==="mobile" ? 44 : 0 }}>
                <MobScreen/>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
